/**
 * La foto de una persona del equipo, que se cambia con un clic: la cámara sale
 * al pasar por encima, se elige de la biblioteca y se guarda en el momento.
 *
 * La usan el asistente del servicio y su ficha («Lo da»): la foto es de la
 * persona y no del servicio, así que cambiarla aquí la cambia en todas partes.
 */
import { useState } from 'react'
import { IconCamera, IconLoader2 } from '@tabler/icons-react'
import { useI18n } from '../../../lib/i18n'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { useToast } from '../../Toast'
import { bookingError } from './bookingShared'
import { updateBookingResource, type BookingResource } from '../../../lib/api'

export function TeamPhotoButton({ projectId, token, member, className = '', onChanged }: {
  projectId: string
  token: string
  member: Pick<BookingResource, 'id' | 'name' | 'photoUrl'>
  className?: string
  /** Ya guardada: quien la usa actualiza su lista del equipo. */
  onChanged: (photoUrl: string) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [picking, setPicking] = useState(false)
  const [saving, setSaving] = useState(false)
  const label = t('bookings.team.photo', { name: member.name })
  const initials = member.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] ?? '').join('').toUpperCase()

  return (
    <>
      <button
        type="button"
        className={`dash-team-photo-edit ${className}`.trim()}
        aria-label={label}
        title={label}
        disabled={saving}
        onClick={(event) => { event.stopPropagation(); setPicking(true) }}
      >
        {member.photoUrl ? <img src={member.photoUrl} alt="" /> : <span>{initials}</span>}
        <i aria-hidden="true" className={saving ? 'is-busy' : ''}>
          {saving ? <IconLoader2 className="dash-spin" size={13} /> : <IconCamera size={13} />}
        </i>
      </button>
      {picking ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onSelect={(url) => {
            setPicking(false)
            setSaving(true)
            updateBookingResource(projectId, member.id, { photoUrl: url }, token)
              .then(() => onChanged(url))
              .catch((cause: unknown) => showToast(bookingError(cause), 'error'))
              .finally(() => setSaving(false))
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </>
  )
}
